import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { useParams } from "react-router";

type Notification = {
	id: string;
	userId: string;
	type: string;
	title: string;
	body: string;
	metadata: string | null;
	read: boolean;
	createdAt: string;
};

type NotificationContextValue = {
	notifications: Notification[];
	unreadCount: number;
	loading: boolean;
	markRead: (id: string) => Promise<void>;
	markAllRead: () => Promise<void>;
	refetch: () => Promise<void>;
};

const NotificationContext = createContext<NotificationContextValue | null>(
	null,
);

const POLL_INTERVAL = 30_000;

export function NotificationProvider({ children }: { children: ReactNode }) {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const [notifications, setNotifications] = useState<Notification[]>([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [loading, setLoading] = useState(true);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const fetchNotifications = useCallback(async () => {
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/notifications`, {
				credentials: "include",
			});
			if (!res.ok) return;
			const data = (await res.json()) as {
				notifications: Notification[];
				unreadCount: number;
				nextCursor: string | null;
			};
			setNotifications(data.notifications);
			setUnreadCount(data.unreadCount);
		} catch {
			// swallow network errors
		} finally {
			setLoading(false);
		}
	}, [workspaceSlug]);

	useEffect(() => {
		fetchNotifications();
		intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL);
		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, [fetchNotifications]);

	const markRead = useCallback(
		async (id: string) => {
			try {
				await fetch(`/api/tenant/${workspaceSlug}/notifications/${id}/read`, {
					method: "PATCH",
					credentials: "include",
				});
				setNotifications((prev) =>
					prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
				);
				setUnreadCount((prev) => Math.max(0, prev - 1));
			} catch {
				// swallow
			}
		},
		[workspaceSlug],
	);

	const markAllRead = useCallback(async () => {
		try {
			await fetch(`/api/tenant/${workspaceSlug}/notifications/read-all`, {
				method: "POST",
				credentials: "include",
			});
			setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
			setUnreadCount(0);
		} catch {
			// swallow
		}
	}, [workspaceSlug]);

	return (
		<NotificationContext.Provider
			value={{
				notifications,
				unreadCount,
				loading,
				markRead,
				markAllRead,
				refetch: fetchNotifications,
			}}
		>
			{children}
		</NotificationContext.Provider>
	);
}

export function useNotifications() {
	const ctx = useContext(NotificationContext);
	if (!ctx)
		throw new Error(
			"useNotifications must be used within NotificationProvider",
		);
	return ctx;
}
